import { useCallback, useEffect, useRef, useState } from 'react';

import type { CsrPlayerElement } from '@spotify-internal/csr-player';
import { useLocalStorage } from '@spotify/confidence-core-react';

const SKIP_INACTIVE_KEY = 'recordings-skip-inactive';
const SPEED_KEY = 'recordings-playback-speed';

// timeupdate fires every animation frame while playing; committing every
// event re-renders the whole controls subtree continuously. Only commit once
// the displayed time has moved enough to be visible.
const TIME_UPDATE_INTERVAL_MS = 250;

export type PlaybackDefaults = {
  speed?: number;
  skipInactive?: boolean;
};

type PlaybackState = {
  isPlaying: boolean;
  currentTimeMs: number;
  totalTimeMs: number;
  speed: number;
  skipInactive: boolean;
  handleSeek: (_: unknown, value: number | number[]) => void;
  handleSeekBySeconds: (seconds: number) => void;
  handleSpeedChange: (speed: number) => void;
  handleToggleSkipInactive: (checked: boolean) => void;
};

export const usePlaybackState = (
  player: CsrPlayerElement,
  options?: { defaults?: PlaybackDefaults },
): PlaybackState => {
  const [storedSpeed, setStoredSpeed] = useLocalStorage<number | null>(
    SPEED_KEY,
    null,
  );
  const [storedSkipInactive, setStoredSkipInactive] = useLocalStorage(
    SKIP_INACTIVE_KEY,
    false,
  );

  const [isPlaying, setIsPlaying] = useState(!player.paused);
  const [currentTimeMs, setCurrentTimeMs] = useState(player.currentTime * 1000);
  const [speed, setSpeed] = useState(
    () => options?.defaults?.speed ?? storedSpeed ?? player.playbackRate,
  );
  const [skipInactive, setSkipInactive] = useState<boolean>(
    options?.defaults?.skipInactive ?? storedSkipInactive,
  );

  const totalTimeMs = player.duration * 1000;

  // First-render snapshot, applied once per player in the effect below.
  // Later changes write the player directly in their handlers — re-applying
  // both settings on every change would spam the replayer with redundant
  // setConfig calls.
  const initialSettingsRef = useRef({
    speed: options?.defaults?.speed ?? storedSpeed ?? undefined,
    skipInactive,
  });

  useEffect(() => {
    const initial = initialSettingsRef.current;
    player.skipInactive = initial.skipInactive;
    if (initial.speed !== undefined) {
      player.playbackRate = initial.speed;
    }

    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setCurrentTimeMs(player.currentTime * 1000);
    };
    const onTimeUpdate = () => {
      const nextTimeMs = player.currentTime * 1000;
      setCurrentTimeMs(prev =>
        Math.abs(nextTimeMs - prev) < TIME_UPDATE_INTERVAL_MS
          ? prev
          : nextTimeMs,
      );
    };

    setCurrentTimeMs(player.currentTime * 1000);
    player.addEventListener('play', onPlay);
    player.addEventListener('pause', onPause);
    player.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      player.removeEventListener('play', onPlay);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [player]);

  const handleSeek = useCallback(
    (_: unknown, value: number | number[]) => {
      const timeMs = Array.isArray(value) ? value[0] : value;
      setCurrentTimeMs(timeMs);
      player.currentTime = timeMs / 1000;
    },
    [player],
  );

  const handleSeekBySeconds = useCallback(
    (seconds: number) => {
      const nextTimeSeconds = Math.min(
        Math.max(player.currentTime + seconds, 0),
        player.duration,
      );
      player.currentTime = nextTimeSeconds;
      setCurrentTimeMs(nextTimeSeconds * 1000);
    },
    [player],
  );

  const handleSpeedChange = useCallback(
    (newSpeed: number) => {
      setSpeed(newSpeed);
      player.playbackRate = newSpeed;
      setStoredSpeed(newSpeed);
    },
    [player, setStoredSpeed],
  );

  const handleToggleSkipInactive = useCallback(
    (checked: boolean) => {
      setSkipInactive(checked);
      player.skipInactive = checked;
      setStoredSkipInactive(checked);
    },
    [player, setStoredSkipInactive],
  );

  return {
    isPlaying,
    currentTimeMs,
    totalTimeMs,
    speed,
    skipInactive,
    handleSeek,
    handleSeekBySeconds,
    handleSpeedChange,
    handleToggleSkipInactive,
  };
};
